import type { Momento } from "@caint/engine";
import { useMemo, useState } from "react";
import { imgqComPeso, mediaScoreClasse, planoMeta } from "../../lib/analiseRelatorio.ts";
import { engine } from "../../lib/engine.ts";
import type { ItemMerged } from "../../state/useAppState.ts";

const META = 0.46;
const banda = (v: number | null): string =>
  v == null ? "var(--mat-na)" : v >= 0.6 ? "var(--success)" : v >= META ? "var(--warning)" : "var(--danger)";
const CHIP_EIXO: Record<string, string> = {
  IMGO: "var(--mat-3)",
  IMQA: "var(--mat-0)",
  IMGR: "var(--mat-2)",
};

/**
 * Simulador de meta — roda sobre os itens REAIS do ciclo (maturidade + peso de
 * cada item). Mostra o IMGQ atual, o plano de menor esforço para bater 46% e o
 * efeito (exploratório) de mudar o peso do CORE. O peso do CORE no sistema é fixo
 * em 3; o slider é só didático.
 */
export function SimuladorMeta({ items, momento }: { items: ItemMerged[]; momento: Momento }) {
  const [passos, setPassos] = useState(0);
  const [pesoCore, setPesoCore] = useState(3);

  const plano = useMemo(() => planoMeta(items, momento, META), [items, momento]);
  const media = useMemo(() => mediaScoreClasse(items, momento), [items, momento]);

  // aplica os N primeiros passos do plano (override da maturidade dos itens)
  const overrides = useMemo(() => {
    const m = new Map<string, number>();
    for (let i = 0; i < passos && i < plano.passos.length; i++) {
      const p = plano.passos[i];
      if (p) m.set(p.itemId, p.para);
    }
    return m;
  }, [passos, plano]);

  const proposto = useMemo(() => {
    const mats = items.map((it) => ({
      mi: overrides.has(it.id) ? String(overrides.get(it.id)) : it[momento],
      peso: it.peso,
      eixo: it.eixo,
    }));
    return imgqComPeso(mats, pesoCore);
  }, [items, momento, overrides, pesoCore]);

  const atual = plano.imgqInicial;
  const bateu = proposto.efetivo >= META;
  const falta = Math.max(0, META - atual);
  const nAval = media.nCore + media.nNaoCore;
  const passoMeta = plano.passos.findIndex((p) => p.imgqApos >= META); // 0-based
  const reduzirSobe = media.core != null && media.naoCore != null && media.core < media.naoCore;

  return (
    <section className="m-card pad rel-sec">
      <div className="rel-sec-head">
        <h3>Simulador de meta (dados reais deste ciclo)</h3>
        <span className="spec">{nAval} itens avaliados</span>
      </div>

      {/* Diagnóstico atual */}
      <div className="rel-stats">
        <div className="rel-stat">
          <span className="l">IMGQ atual</span>
          <span className="v" style={{ color: banda(atual) }}>
            {engine.pct(atual)}
          </span>
          <span className="f">{plano.jaBate ? "já bate a meta" : `faltam ${engine.pct(falta)}`}</span>
        </div>
        <div className="rel-stat">
          <span className="l">CORE em 0/1</span>
          <span
            className="v"
            style={{ color: proposto.core0 + proposto.core1 > 0 ? "var(--danger)" : undefined }}
          >
            {proposto.core0 + proposto.core1}
          </span>
          <span className="f">travam a Prata</span>
        </div>
        <div className="rel-stat">
          <span className="l">CORE médio</span>
          <span className="v">{media.core == null ? "—" : engine.num2(media.core)}</span>
          <span className="f">score (peso 3)</span>
        </div>
        <div className="rel-stat">
          <span className="l">Não-CORE médio</span>
          <span className="v">{media.naoCore == null ? "—" : engine.num2(media.naoCore)}</span>
          <span className="f">score</span>
        </div>
      </div>

      <p className="spec rel-nota">
        {media.core != null && media.naoCore != null ? (
          <>
            CORE médio ({engine.num2(media.core)}) {reduzirSobe ? "<" : "≥"} não-CORE (
            {engine.num2(media.naoCore)}) →{" "}
            {reduzirSobe
              ? "reduzir o peso do CORE subiria o número — mas a trava (CORE em 0/1) permanece e isso "
              : "reduzir o peso do CORE desceria o número (os CORE estão melhores que a média). "}
            {reduzirSobe ? "mascara o essencial. " : ""}O peso do CORE no sistema é fixo em 3.
          </>
        ) : null}
      </p>

      {/* Exploração: peso do CORE */}
      <h4 className="rel-h4">Explorar — peso do CORE (didático; no sistema é 3)</h4>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <input
          type="range"
          min={1}
          max={5}
          step={1}
          value={pesoCore}
          onChange={(ev) => setPesoCore(Number(ev.target.value))}
          style={{ flex: 1, minWidth: 120 }}
        />
        <b style={{ fontFamily: "var(--font-mono)", minWidth: 20 }}>{pesoCore}</b>
        <span className="spec">
          IMGQ com este peso: <b style={{ color: banda(proposto.efetivo) }}>{engine.pct(proposto.efetivo)}</b>
        </span>
      </div>

      {/* Plano de menor esforço */}
      <h4 className="rel-h4">Plano de menor esforço para bater 46%</h4>
      {plano.jaBate ? (
        <p className="spec">
          ✓ O ciclo já bate a meta (Bronze).{" "}
          {proposto.core0 + proposto.core1 > 0
            ? `Para chegar a Prata, destrave subindo ${proposto.core0 + proposto.core1} CORE de 0/1 para ≥ 2.`
            : "Sem CORE em 0/1 — pode evoluir para Prata."}
        </p>
      ) : plano.passos.length === 0 ? (
        <p className="spec">Não há itens avaliados suficientes para simular um plano.</p>
      ) : (
        <>
          <p className="spec rel-nota">
            {plano.atingiu
              ? `Subindo ${passoMeta + 1} item(ns) (o de maior ganho primeiro) o IMGQ cruza os 46%. `
              : "Mesmo subindo todos os itens avaliáveis, o cenário não cruza 46% — reavalie o escopo. "}
            Arraste para aplicar os passos e ver o IMGQ subir:
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "8px 0" }}>
            <input
              type="range"
              min={0}
              max={plano.passos.length}
              step={1}
              value={passos}
              onChange={(ev) => setPassos(Number(ev.target.value))}
              style={{ flex: 1 }}
            />
            <b
              style={{
                fontFamily: "var(--font-mono)",
                minWidth: 66,
                textAlign: "right",
                color: banda(proposto.efetivo),
              }}
            >
              {engine.pct(proposto.efetivo)}
            </b>
            {passoMeta >= 0 ? (
              <button type="button" className="m-btn" onClick={() => setPassos(passoMeta + 1)}>
                Até a meta
              </button>
            ) : null}
          </div>
          <div className="rel-table-wrap">
            <table className="rel-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item</th>
                  <th>Eixo</th>
                  <th className="num">Peso</th>
                  <th>Subir</th>
                  <th className="num">IMGQ após</th>
                </tr>
              </thead>
              <tbody>
                {plano.passos.slice(0, 20).map((p, i) => {
                  const aplicado = i < passos;
                  const cruza = i === passoMeta;
                  return (
                    <tr
                      key={`${p.itemId}-${i}`}
                      style={aplicado ? { background: "var(--surface-2)" } : undefined}
                    >
                      <td>{i + 1}</td>
                      <td>{p.code}</td>
                      <td>
                        <span
                          className="rel-badge"
                          style={{ background: CHIP_EIXO[p.eixo], fontSize: 10.5, padding: "1px 7px" }}
                        >
                          {p.eixo}
                        </span>
                      </td>
                      <td className="num">
                        {p.peso === 3 ? (
                          <b style={{ color: "var(--mat-0)" }}>CORE</b>
                        ) : p.peso === 2 ? (
                          "Imp."
                        ) : (
                          "Desej."
                        )}
                      </td>
                      <td>
                        N{p.de} → N{p.para}
                      </td>
                      <td
                        className="num"
                        style={cruza ? { color: "var(--success)", fontWeight: 700 } : undefined}
                      >
                        {engine.pct(p.imgqApos)}
                        {cruza ? " ✓" : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {plano.passos.length > 20 ? (
            <p className="spec rel-nota">… e mais {plano.passos.length - 20} passo(s) no plano completo.</p>
          ) : null}
          <p className="spec rel-nota">
            Prioridade por <b>maior ganho por item</b>: CORE (peso 3) e saltos de nível 1→2 / 2→3 primeiro.
            Este é o caminho <b>legítimo</b> para subir o IMGQ — e, ao levar CORE de 0/1 para ≥ 2, também{" "}
            <b>destrava</b> o selo.
          </p>
        </>
      )}
    </section>
  );
}
